import { DeleteOutlined } from '@ant-design/icons';
import { Button, Popconfirm, PopconfirmProps } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-relay';

import DeleteBulkScheduleItemMutation from '../../graphql/mutations/DeleteBulkScheduleItemMutation';
import DeleteScheduleItemMutation from '../../graphql/mutations/DeleteScheduleItemMutation';
import useCommitScheduleItem from '../../hooks/useCommitScheduleItem';
import { useRouting } from '../../routing/routingProvider';
import { ComponentsDispatch, RootState } from '../../store';
import { setterGantItems } from '../../store/slices/gantItemsSlice';
import { setterGant } from '../../store/slices/gantSlice';
import { addSpinnerItem, removeSpinnerItem } from '../../store/slices/spinnerSlice';
import { ItemType, MappingItemIdIndexType } from '../../types';

interface IControlPanelItem {
    moveItems: (itemsForMoving: ItemType[], newItems: ItemType[]) => ItemType[];
    historyDeletedChildren: Set<string>;
}

const ControlPanelItem: React.FC<IControlPanelItem> = ({ moveItems, historyDeletedChildren }) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const [commitDelete, isInFlightDelete] = useMutation(DeleteScheduleItemMutation);
    const [commitBulkDelete, isInFlightBulkDelete] = useMutation(DeleteBulkScheduleItemMutation);
    const { getCommitScheduleItemDelete, getCommitScheduleBulkItemDelete } = useCommitScheduleItem();
    const connectionItemsId = useSelector<RootState, string>((state) => state.gant.connectionItemsId);

    const currentGantItem = useSelector<RootState, ItemType | null>((state) => state.gantItems.currentGantItem);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);
    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);

    const [listDeletedId, setListDeletedId] = useState<string[]>([]);
    const { getParams, setUrlParam } = useRouting();

    useEffect(() => {
        if (isInFlightDelete || isInFlightBulkDelete) {
            dispatch(addSpinnerItem('deleteScheduleItem'));
        } else {
            dispatch(removeSpinnerItem('deleteScheduleItem'));
        }
    }, [isInFlightDelete, isInFlightBulkDelete]);

    useEffect(() => {
        if (!listDeletedId.length) {
            return;
        }

        setListDeletedId([]);

        // setTimeout needs for avoiding infinity loop
        setTimeout(() => {
            const newItems = moveItems(itemsList, itemsList);

            dispatch(
                setterGantItems({
                    itemsList: newItems,
                    // countVisibleRow: newItems.filter((item) => !item.hideForCollapse).length
                }),
            );
        });
    }, [itemsList]);

    const description = () => {
        const message = 'Are you sure to delete this item';
        if (currentGantItem?.children?.length) {
            const listItemName = currentGantItem.children
                .map((itemId) => itemsList[mappingItemIdIndex[itemId]]?.name)
                .filter((itemId) => itemId)
                .join(', ');
            return `${message} and all children that item include: ${listItemName}?`;
        }

        return message + '?';
    };

    const updateDependenciesForDeletedItemAndResetCurrentItem = (deletedId?: string | string[]) => {
        setListDeletedId(Array.isArray(deletedId) ? deletedId : [deletedId as string]);

        setTimeout(() => {
            dispatch(
                setterGantItems({
                    currentGantItem: null,
                    isItemEdit: false,
                }),
            );
            dispatch(
                setterGant({
                    savingProcess: 'none',
                }),
            );
        });
    };

    const onClickDelete: PopconfirmProps['onConfirm'] = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e?.stopPropagation();

        if (currentGantItem?.id) {
            dispatch(addSpinnerItem('deleteScheduleItem'));
            const { item } = getParams();
            if (currentGantItem.id === item) {
                setUrlParam({
                    item: undefined,
                });
            }

            if (currentGantItem?.children?.length) {
                const filteredChildId = currentGantItem.children.filter(
                    (childId) => !historyDeletedChildren.has(childId),
                );
                commitBulkDelete(
                    getCommitScheduleBulkItemDelete(
                        [...filteredChildId, currentGantItem?.id],
                        connectionItemsId,
                        updateDependenciesForDeletedItemAndResetCurrentItem,
                    ),
                );
            } else {
                commitDelete(
                    getCommitScheduleItemDelete(
                        currentGantItem?.id!,
                        connectionItemsId,
                        updateDependenciesForDeletedItemAndResetCurrentItem,
                    ),
                );
            }
        }
    };

    return (
        <>
            <Popconfirm
                overlayStyle={{ width: 350 }}
                title={`Delete the item ${currentGantItem?.name}`}
                description={description()}
                onConfirm={onClickDelete}
                okText="Yes"
                cancelText="No"
            >
                <Button
                    type="default"
                    danger
                    htmlType="submit"
                    icon={<DeleteOutlined />}
                    style={{ width: 100, alignSelf: 'end' }}
                >
                    Delete
                </Button>
            </Popconfirm>
            {/*<Button type="default" htmlType="submit" icon={<CopyOutlined />} style={{ width: '30%', alignSelf: 'end' }}>*/}
            {/*    Duplicate*/}
            {/*</Button>*/}
        </>
    );
};

export default React.memo(ControlPanelItem);
